import { bool, string, number } from "prop-types";
import React, { useState } from "react";
import { twMerge } from "tailwind-merge";

import { useTranslation } from "@lib/i18n";

import Typography from "@components/shared/Typography";
import FinanceTerms from "@components/shared/FinanceTerms";

import { getFormattedPrice } from "@shared/v2/lib/getFormattedPrice";

import { useVACPriceBreakdownSelector } from "@redux/reducers/deal";
import { withTabNavigation } from "@hoc/withTabNavigation";

const formatText = (text, quantity) => {
  return text && text.replace(/\[(\/)?b\]/g, "").replace("{0}", quantity);
};

const ClickableTypography = withTabNavigation(Typography);

const VACPriceDetailv2 = ({
  isFinanceRequestError,
  amount,
  priceFormat,
  currencySymbol,
  isQuantityVisible,
  vehicleQuantity,
  apr,
  isPersonalised,
}) => {
  const [isFinanceTermsOpen, setIsFinanceTermsOpen] = useState(false);

  const { t } = useTranslation();

  const breakdown = useVACPriceBreakdownSelector();

  const getTeaser = () => breakdown?.vehicleSimulation?.result?.teaser;

  const formattedAmount = () =>
    getFormattedPrice(amount, priceFormat, currencySymbol);

  const aprWithPercentage = apr?.toString().includes("%") ? apr : `${apr}%`;

  return (
    <>
      {isFinanceRequestError ? (
        <Typography className="text-white text-lg text-center font-semibold m-auto max-w-280 px-4">
          {t("financeDataErrorMessage")}
        </Typography>
      ) : (
        <div
          className={twMerge(
            "flex flex-col justify-center items-end h-full px-0 py-2 z-10 text-white",
            "sm:p-4 md:p-2 lg:px-0 lg:py-1 md:items-start"
          )}
          data-id="price-switcher-price-container"
        >
          <div
            className="text-white text-2xl font-semibold sm:text-3xl md:text-2xl lg:text-3xl"
            data-id="price-switcher-formatted-price"
          >
            {isQuantityVisible && vehicleQuantity > 1 && (
              <span className="text-lg">
                {vehicleQuantity}{" "}
                <span className="text-xxs font-extrabold">x</span>{" "}
              </span>
            )}
            {!isPersonalised && (
              <span className="text-xs normal-case">
                {t("price.vac.startingFrom")}
              </span>
            )}
            {formattedAmount()}
            <span className="text-xs normal-case">
              {t("price.vac.perMonth")}*
            </span>
            <Typography className="text-white text-xs/4 font-semibold normal-case">
              {formatText(
                getTeaser()?.primarytext?.[0].text,
                aprWithPercentage
              )}
            </Typography>
            <ClickableTypography
              className="text-white text-xxs/4 font-semibold normal-case cursor-pointer underline"
              onClick={() => setIsFinanceTermsOpen(true)}
            >
              {formatText(getTeaser()?.callToAction.detailText)}
            </ClickableTypography>
          </div>
          <FinanceTerms
            isOpen={isFinanceTermsOpen}
            onClose={() => setIsFinanceTermsOpen(false)}
          />
        </div>
      )}
    </>
  );
};

VACPriceDetailv2.propTypes = {
  priceFormat: string.isRequired,
  isFinanceRequestError: bool,
  withEcoBonus: bool,
  amount: number,
  deposit: number,
  currencySymbol: string.isRequired,
  withDeliveryInfo: bool,
  withFinanceWidgetButton: bool,
  isQuantityVisible: bool,
  vehicleQuantity: number,
  isUIv2: bool,
  apr: number,
  isBasketUi: bool,
  isPersonalised: bool,
};

export default VACPriceDetailv2;
